import React from "react";
import InputLabel from "./InputLabel";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import useTranslation from "../CustomHooks/useTranslation";
import { inputFieldContent } from "./InputFieldContent";
import InputShimmer from "./InputShimmer";

interface InputItem {
  labelName: string;
  id: string;
}

interface NumberInputFieldProps {
  inputItem: InputItem;
  errorMessage?: string;
  required?: boolean;
  onChange?: (id: string, value: number | string) => void;
  fieldError?: string | boolean;
  tabIndex: number;
  maxLength?: number;
  testId?: string;
  disabled?: boolean;
  value?: number | string;
  className?: string;
  maindivstyle?: string;
  editLoading?: boolean;
  placeholder?: string;
  secondaryText?: string;
  percentage?: boolean;
  customWidth?: boolean;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  step?: string;
  hideSecondaryText?: boolean;
  customModal?: boolean;
}

const NumberInputField: React.FC<NumberInputFieldProps> = ({
  inputItem,
  errorMessage,
  onChange,
  required,
  fieldError,
  maxLength,
  testId,
  disabled,
  value,
  className = '',
  maindivstyle = '',
  editLoading,
  placeholder,
  secondaryText,
  percentage,
  customWidth,
  min,
  max,
  decimalPlaces = 2,
  step,
  hideSecondaryText,
  customModal
}) => {
  const { labelName, id } = inputItem;
  const { translate } = useTranslation(inputFieldContent);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    if (input === '') {
      if (onChange) {
        onChange(id, '');
      }
      return;
    }

    if (input === '-') {
      if (min === undefined || min < 0) {
        if (onChange) {
          onChange(id, '-');
        }
      }
      return;
    }

    // Handle decimal point
    if ((input.match(/^-?\d*\.$/) !== null)) {
      if (onChange) {
        onChange(id, input);
      }
      return;
    }

    // Special handling for percentage fields
    if (percentage) {
      if (customModal) {
        // For custom modal, handle values after 100
        const numericValue = Number(input);
        if (!isNaN(numericValue)) {
          if (input.startsWith('100')) {
            // If input starts with 100, format it as 100.00
            if (onChange) {
              onChange(id, '100.00');
            }
            return;
          }
          if (numericValue > 100) {
            const strValue = String(input);
            let slicedValue = strValue;
            while (Number(slicedValue) > 100 && slicedValue.length > 0) {
              slicedValue = slicedValue.slice(0, -1);
            }
            if (onChange) {
              onChange(id, slicedValue);
            }
            return;
          }
        }
      }

      // Pattern to validate percentage format:
      // - Allow numbers from 0.00 to 100.00
      const percentageRegex = /^(100(\.0{0,2})?|[0-9]{1,2}(\.\d{0,2})?)$/;
      
      if (!percentageRegex.test(input)) {
        event.preventDefault();
        return;
      }

      const numericValue = Number(input);
      if (!isNaN(numericValue)) {
        if (numericValue > 100) {
          event.preventDefault();
          return;
        }
        
        if (min !== undefined && numericValue < min) {
          // Still allow typing, validation will happen on blur or submit
        }
      }
    } else {
      // For non-percentage numbers
      // Replace leading zero with the new number
      if (input.match(/^0[0-9]+/)) {
        if (onChange) {
          onChange(id, input.slice(1));
        }
        return;
      }

      const regex = new RegExp(`^-?\\d*\\.?\\d{0,${decimalPlaces}}`);
      if (!regex.test(input)) {
        event.preventDefault();
        return;
      }
      
      const numericValue = parseFloat(input);
      if (!isNaN(numericValue)) {
        if (numericValue > 100) {
          event.preventDefault();
          return;
        }
        if (max !== undefined && numericValue > max) {
          event.preventDefault();
          return;
        }
      }
    }

    if (onChange) {
      onChange(id, input);
    }
  };

  return (
    <div className={maindivstyle}>
      <InputLabel labelName={labelName} id={id} required={required} />
      {editLoading ? (
        <InputShimmer className={className} />
      ) : (
        <>
          <div className="relative">
            <input
              id={id}
              data-testid={testId || id}
              className={`${className} disabled:cursor-not-allowed ${customWidth || maindivstyle ? 'w-full' : 'w-80'} h-14 py-5 pr-${percentage ? '10' : '[15px]'} pl-2.5 focus:pl-[9px] disabled:bg-disable_fill disabled:border-disable_stroke border rounded-lg text-subTitle outline-hidden focus:border-2 ${errorMessage || fieldError
                  ? "border-negative"
                  : "border-P_light_active"
                }`}
              placeholder={placeholder ? translate(placeholder) : translate(`Enter ${labelName.toLowerCase()}`)}
              onChange={handleChange}
              // onKeyDown={handleKeyDown}
              autoComplete="off"
              maxLength={percentage ? 6 : maxLength} // For percentage: max 3 digits + decimal + 2 decimals (100.00)
              disabled={disabled}
              value={value}
              type="text"
              // inputMode="decimal"
              min={min}
              max={max}
              step={step}
            />
            {errorMessage && !disabled && (
              <div className={`mb-1`}>
                <ErrorMessage
                  errorMessage={errorMessage}
                  testId={`error-${testId || id}`}
                />
              </div>
            )}
            {secondaryText && (!errorMessage || !errorMessage.includes("Percentage must not exceed")) && !hideSecondaryText && (
              <p className="mt-1 text-body2 text-secondary_text">{secondaryText}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NumberInputField;